import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Download as DownloadIcon,
  BookOpen,
  FileText,
  MonitorPlay,
} from "lucide-react";
import { toast } from "sonner";

/* ==========================================================
   Techno Sort Download Configuration
   ========================================================== */

const _d = (s) => { try { return atob(s); } catch (_) { return ""; } };

const FREE_INSTALLER = {
  label: "Techno Sort Free Trial",
  filename: "TechnoSort_Setup_v1.12.26.zip",
  size: "84 MB",
  version: "1.12.26",
  urlEnc: "aHR0cHM6Ly9wdWItMDJmM2MxYjk2NzlhNDE3ODg0YTA5ZGNhNTVhYWZkODIucjIuZGV2L1RlY2hub1NvcnRfU2V0dXBfdjEuMTIuMjYuemlw",
};

const DOC_PDFS = [
  {
    key: "installation-guide",
    label: "Installation Guide",
    filename: "Techno_Sort_Installation_Guide.pdf",
    size: "2 MB",
    blurb:
      "Complete step-by-step installation instructions for Techno Sort on Windows.",
    urlEnc: "aHR0cHM6Ly9wdWItMDJmM2MxYjk2NzlhNDE3ODg0YTA5ZGNhNTVhYWZkODIucjIuZGV2L1RlY2hub19Tb3J0X0luc3RhbGxhdGlvbl9HdWlkZS5wZGY=",
  },
  {
    key: "system-requirements",
    label: "System Requirements",
    filename: "Techno_Sort_System_Requirements.pdf",
    size: "1 MB",
    blurb:
      "Download the complete hardware and software requirements to ensure optimal performance of Techno Sort.",
    urlEnc: "aHR0cHM6Ly9wdWItMDJmM2MxYjk2NzlhNDE3ODg0YTA5ZGNhNTVhYWZkODIucjIuZGV2L1RlY2hub19Tb3J0X1N5c3RlbV9SZXF1aXJlbWVudHMucGRm",
  },
];

function triggerDownload(asset) {
  const openUrl = asset.urlEnc ? _d(asset.urlEnc) : asset.url;
  if (openUrl) {
    window.open(openUrl, "_blank", "noopener,noreferrer");
    toast.success(`${asset.label} download started`);
    return;
  }

  const content = `${asset.label}

Version: ${asset.version || "1.0.0"}

Placeholder`;

  const blob = new Blob([content], {
    type: "application/octet-stream",
  });

  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = asset.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);

  toast.success(`${asset.label} download started`);
}

export default function Download() {
  return (
    <section
      className="max-w-7xl mx-auto px-6 lg:px-8 py-24"
      data-testid="download-page"
    >
      <h1 className="font-display font-bold text-4xl md:text-6xl text-white">
        Download <span className="text-[#F4A22A]">Techno Sort</span>
      </h1>

      <p className="text-neutral-400 mt-4 max-w-2xl">
        Download the free trial of Techno Sort with a 300-document limit.
        Download the Installation Guide and System Requirements before
        deployment.
        Supports Windows 10, Windows 11, Windows Server 2019, 2022 and 2025.
      </p>

      <div className="mt-10">
        <Tabs
          defaultValue="free"
          className="w-full"
          data-testid="download-tabs"
        >
          <TabsList className="bg-transparent border border-white/10 rounded-none p-0 h-auto w-full md:w-auto inline-flex flex-wrap">
            <TabsTrigger
              value="free"
              data-testid="download-tab-free"
              className="rounded-none px-6 py-3 data-[state=active]:bg-[#F4A22A] data-[state=active]:text-black text-neutral-300 uppercase text-xs tracking-[0.2em] font-bold"
            >
              Free Trial · 300 Docs
            </TabsTrigger>

            <TabsTrigger
              value="docs"
              data-testid="download-tab-docs"
              className="rounded-none px-6 py-3 data-[state=active]:bg-[#F4A22A] data-[state=active]:text-black text-neutral-300 uppercase text-xs tracking-[0.2em] font-bold"
            >
              Documentation
            </TabsTrigger>
          </TabsList>

          <TabsContent value="free" className="mt-6">
            <div
              className="card-tile p-10"
              data-testid="download-free-card"
            >
              <div className="flex items-start gap-6 flex-wrap">
                <div className="w-14 h-14 bg-black border border-white/10 flex items-center justify-center">
                  <DownloadIcon className="w-7 h-7 text-[#F4A22A]" />
                </div>

                <div className="flex-1 min-w-[260px]">
                  <div className="font-display font-bold text-2xl text-white">
                    {FREE_INSTALLER.label}
                  </div>

                  <p className="text-neutral-400 text-sm mt-2 max-w-xl">
                    Download the free trial with support for up to 300
                    documents.
                    Upgrade anytime from the{" "}
                    <Link
                      to="/pricing"
                      className="text-[#F4A22A] underline"
                    >
                      Pricing Page
                    </Link>
                    .
                  </p>

                  <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-6 text-xs">
                    <div>
                      <div className="text-neutral-500 uppercase tracking-[0.2em]">
                        Version
                      </div>

                      <div className="text-white mt-1 font-mono">
                        {FREE_INSTALLER.version}
                      </div>
                    </div>

                    <div>
                      <div className="text-neutral-500 uppercase tracking-[0.2em]">
                        Size
                      </div>

                      <div className="text-white mt-1">
                        {FREE_INSTALLER.size}
                      </div>
                    </div>

                    <div>
                      <div className="text-neutral-500 uppercase tracking-[0.2em]">
                        Requirements
                      </div>

                      <div className="text-white mt-1">
                        Windows 10/11 · 16 GB RAM
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => triggerDownload(FREE_INSTALLER)}
                  data-testid="download-button-free"
                  className="btn-primary px-7 py-4 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2"
                >
                  <DownloadIcon className="w-4 h-4" />
                  Download Free
                </button>
              </div>
            </div>
          </TabsContent>
          {/* DOCUMENTATION TAB */}
          <TabsContent value="docs" className="mt-6">
            <div
              className="space-y-5"
              data-testid="download-docs-list"
            >
              {DOC_PDFS.map((d) => (
                <div
                  key={d.key}
                  className="card-tile p-8"
                  data-testid={`download-doc-${d.key}`}
                >
                  <div className="flex items-start gap-6 flex-wrap">

                    <div className="w-14 h-14 bg-black border border-white/10 flex items-center justify-center">
                      {d.key === "installation-guide" ? (
                        <BookOpen className="w-7 h-7 text-[#F4A22A]" />
                      ) : (
                        <FileText className="w-7 h-7 text-[#1E5BA8]" />
                      )}
                    </div>

                    <div className="flex-1 min-w-[260px]">

                      <div className="font-display font-bold text-xl text-white">
                        {d.label}
                      </div>

                      <p className="text-neutral-400 text-sm mt-2 max-w-xl">
                        {d.blurb}
                      </p>

                      <div className="mt-4 flex gap-6 text-xs">

                        <div>
                          <span className="text-neutral-500 uppercase tracking-[0.2em]">
                            Type
                          </span>

                          <span className="text-white ml-2">
                            PDF
                          </span>
                        </div>

                        <div>
                          <span className="text-neutral-500 uppercase tracking-[0.2em]">
                            Size
                          </span>

                          <span className="text-white ml-2">
                            {d.size}
                          </span>
                        </div>

                      </div>
                    </div>

                    <button
                      onClick={() => triggerDownload(d)}
                      data-testid={`download-button-${d.key}`}
                      className="bg-white text-black hover:bg-neutral-200 px-7 py-4 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 transition-colors"
                    >
                      <DownloadIcon className="w-4 h-4" />
                      Download PDF
                    </button>

                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

        </Tabs>
      </div>

      <div className="mt-12 text-xs text-neutral-500 flex items-center gap-2">
        <MonitorPlay className="w-3.5 h-3.5" />

        Need help?

        Watch tutorials on our

        <a
          href="https://www.youtube.com/@Techno_Sort-RD"
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-white ml-1"
        >
          YouTube Channel
        </a>

        · or

        <Link
          to="/contact"
          className="underline hover:text-white"
        >
          Contact Support
        </Link>

      </div>

    </section>
  );
}